import * as React from 'react'

import {
  AsyncSelect,
  type AsyncSelectLoadOptions,
  type IAsyncSelectProps,
  Select,
  SubText,
  Space,
  IconAdd,
  Text,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { VendorPaymentMethodSelectStyled } from './styles/VendorPaymentMethodSelectStyled'
import { paymentMethodRepository } from '../../data-access/repositories/PaymentMethodRepository'
import { type PaymentMethodManagementTypeEnum } from '../../entities/enums/PaymentMethodManagementTypeEnum'
import { type PaymentMethodTypeEnum } from '../../entities/enums/PaymentMethodTypeEnum'
import { PaymentMethod } from '../../entities/PaymentMethod'
import { useFormatPaymentMethodSummary } from '../../hooks/useFormatPaymentMethodSummary'

export interface IVendorPaymentMethodSelectProps extends IAsyncSelectProps<PaymentMethod> {
  type?: PaymentMethodTypeEnum
  category?: PaymentMethodManagementTypeEnum
  currencyId?: number
  vendorId: number
  onAddNew?: () => void
}

export const VendorPaymentMethodSelect = ({
  type,
  category,
  currencyId,
  vendorId,
  onAddNew,
  ...props
}: IVendorPaymentMethodSelectProps) => {
  const intl = useProcurifyIntl()
  const formatPaymentMethodSummary = useFormatPaymentMethodSummary({
    convertCountryCodeToName: false,
    showIdentifier: true,
  })

  const loadOptions: AsyncSelectLoadOptions<PaymentMethod> = async (
    inputValue,
    pagination,
    callback
  ) => {
    try {
      const params = {
        ...(inputValue && { search: inputValue }),
        page_size: 20,
        page: pagination.page,
        type,
        currencyId,
        vendorId,
        category,
      }
      const res = await paymentMethodRepository.getPaymentMethods(params)

      callback({
        options: res.data.map((pm) => new PaymentMethod(pm)),
        pagination: {
          hasMore: res.pagination
            ? res.pagination?.page < res.pagination?.numPages
            : false,
          page: pagination.page + 1,
        },
      })
    } catch (error) {
      callback({
        options: [],
        pagination: {
          hasMore: false,
          page: 1,
        },
      })
    }
  }

  return (
    <VendorPaymentMethodSelectStyled>
      <AsyncSelect<PaymentMethod>
        placeholder={intl.formatMessage(messages.selectFieldPlaceholder)}
        loadOptions={loadOptions}
        cacheOptions
        formatOptionLabel={(option) => (
          <>
            <Space direction='horizontal' size='xsmall' align='center'>
              <Text textEllipsis>{option.name}</Text>
            </Space>
            <SubText textEllipsis>{formatPaymentMethodSummary(option)}</SubText>
          </>
        )}
        getOptionValue={(option) => option.id?.toString()}
        stickies={
          onAddNew
            ? ({ closeMenu }) => {
                return [
                  <Select.StickyOption
                    key={0}
                    onClick={() => {
                      onAddNew()
                      closeMenu()
                    }}
                  >
                    <Space direction='horizontal' size='xsmall' align='center'>
                      <IconAdd size={20} />
                      <SubText bold>
                        {intl.formatMessage(messages.createPaymentMethodLabel)}
                      </SubText>
                    </Space>
                  </Select.StickyOption>,
                ]
              }
            : undefined
        }
        {...props}
      />
    </VendorPaymentMethodSelectStyled>
  )
}
